import { type CertificateRecord, CertificatesScreen, type CrewMember } from '@felix/ui';
import { Button, Group, Modal, Text } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, type CertificateType, officeApi } from '../api.js';
import { CertificateModal } from '../components/CertificateModal.js';
import { ErrorAlert } from '../components/ErrorAlert.js';
import { Shell } from '../components/Shell.js';
import { uploadCrewCertificateFile } from '../crew-files.js';
import { useDashboard } from '../dashboard-data.js';
import { officeSource } from '../personnel-source.js';

/**
 * Sertifikalar ve belgeler (ortak ekran `CertificatesScreen`; ADR 0016 §8): belgeleri ofis yönetir.
 * Ekleme, düzeltme ve kaldırma diyalogları ofise özgüdür; kayıttan sonra ekran ve pano yenilenir.
 */
export function CertificatesPage() {
  const { t } = useTranslation();
  const { refresh } = useDashboard();
  const [types, setTypes] = useState<CertificateType[]>([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [editing, setEditing] = useState<{
    person: CrewMember;
    record: CertificateRecord | null;
    presetTypeId: string | null;
  } | null>(null);
  const [removing, setRemoving] = useState<{
    person: CrewMember;
    record: CertificateRecord;
  } | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  useEffect(() => {
    officeApi
      .certificateTypes()
      .then(setTypes)
      .catch(() => setTypes([]));
  }, []);

  const saved = () => {
    setReloadKey((key) => key + 1);
    void refresh();
  };

  const confirmRemove = async () => {
    if (!removing) return;
    setRemoveError(null);
    try {
      await officeApi.removeCertificate(removing.record.id, removing.record.rowVersion);
      setRemoving(null);
      saved();
    } catch (error) {
      setRemoveError(error instanceof ApiError ? error.code : 'GENERIC');
    }
  };

  return (
    <Shell title={t('certificates.title')}>
      <CertificatesScreen
        source={officeSource}
        reloadKey={reloadKey}
        actions={{
          add: (person, presetTypeId) => setEditing({ person, record: null, presetTypeId }),
          edit: (person, record) => setEditing({ person, record, presetTypeId: null }),
          remove: (person, record) => {
            setRemoveError(null);
            setRemoving({ person, record });
          },
          // Belge dosyası ya da yeni sürümü (ADR 0164); ekran listeyi kendisi yeniden okur
          uploadFile: async (_person, record, file) => {
            await uploadCrewCertificateFile(record.id, file);
          },
        }}
      />

      {editing ? (
        <CertificateModal
          opened
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            saved();
          }}
          person={editing.person}
          types={types}
          record={editing.record}
          presetTypeId={editing.presetTypeId}
        />
      ) : null}

      <Modal
        opened={removing !== null}
        onClose={() => setRemoving(null)}
        title={t('certificates.removeTitle')}
        centered
        radius="md"
        closeButtonProps={{ 'aria-label': t('fleet.closeDialog') }}
      >
        <ErrorAlert code={removeError} />
        <Text fz="sm">{t('certificates.removeText', { name: removing?.record.name ?? '' })}</Text>
        <Group justify="flex-end" gap="sm" mt="lg">
          <Button variant="default" onClick={() => setRemoving(null)}>
            {t('certificates.form.cancel')}
          </Button>
          <Button
            color="red"
            leftSection={<IconTrash size={16} />}
            onClick={() => void confirmRemove()}
          >
            {t('certificates.removeConfirm')}
          </Button>
        </Group>
      </Modal>
    </Shell>
  );
}
